// Pure helpers for the Analytics dashboard.
//
// The dashboard no longer computes its numbers from rows. The API returns six
// aggregates — overview, aging, throughput, denials, spend by entity,
// corrections — already scoped to what the caller may see, and this module only
// orders, labels and null-guards them (docs/app.md → Analytics). Everything
// that used to walk every invoice, decision, flag, line item and correction in the
// browser is deleted with the loaders that fed it.
//
// No I/O here.
import type {
  AgingBand,
  AgingNode,
  AnalyticsCorrections,
  AnalyticsDenials,
  AnalyticsOverview,
  AnalyticsSpendByEntity,
  StatusBucket,
} from '../api/types'
import type { AgeBand } from './format'
import {
  DecisionOutcome,
  InvoiceStatus,
  TIMELINE_NODES,
  accountSourceLabel,
  costCenterSourceLabel,
  flagLabel,
  reasonCategoryLabel,
  type NodeKey,
} from './constants'

/** Count of invoices, or dollars. The toggle every panel that can honour it reads. */
export type Metric = 'count' | 'amount'

// Workflow-order status groupings. Active = anything still moving.
export const ACTIVE_STATUSES: readonly string[] = [
  InvoiceStatus.PendingMatching,
  InvoiceStatus.MatchingFailed,
  InvoiceStatus.PendingImportControl,
  InvoiceStatus.PendingDepartment,
  InvoiceStatus.PendingDirector,
  InvoiceStatus.PendingOversight,
  InvoiceStatus.PendingExportControl,
  InvoiceStatus.PendingExport,
  InvoiceStatus.PendingManualEntry,
  InvoiceStatus.PendingDenialReview,
]

// The two happy-path terminals — the invoice reached Point Click Care (CSV
// batch import at Closing Confirmation, or hand-keyed at Manual Entry).
export const PCC_COMPLETED_STATUSES: readonly string[] = [
  InvoiceStatus.BulkImported,
  InvoiceStatus.ManuallyEntered,
]

export const TERMINAL_STATUSES: readonly string[] = [
  ...PCC_COMPLETED_STATUSES,
  InvoiceStatus.ArchivedWithoutPayment,
  InvoiceStatus.ArchivedForFreshInvoice,
  InvoiceStatus.ArchivedNotAnInvoice,
]

// Short stage labels — the enum values are too long for a chart axis.
export const STAGE_LABELS: Record<string, string> = {
  [InvoiceStatus.PendingMatching]: 'Matching',
  [InvoiceStatus.MatchingFailed]: 'Matching failed',
  [InvoiceStatus.PendingImportControl]: 'Import control',
  [InvoiceStatus.PendingDepartment]: 'Department approval',
  [InvoiceStatus.PendingDirector]: 'Director approval',
  [InvoiceStatus.PendingOversight]: 'Oversight approval',
  [InvoiceStatus.PendingExportControl]: 'Export control',
  [InvoiceStatus.PendingExport]: 'Pending export',
  [InvoiceStatus.PendingManualEntry]: 'Manual entry',
  [InvoiceStatus.PendingDenialReview]: 'Denial review',
}

/** One aggregate bucket's magnitude under the current measure. A bucket the API
    had nothing for is absent rather than zero, so every read of one goes through
    here: `metricValue(null)` is 0, never NaN. */
export function metricValue(
  bucket: { count?: number | null; total?: number | null } | null | undefined,
  metric: Metric = 'count',
): number {
  if (!bucket) return 0
  const raw = metric === 'count' ? bucket.count : bucket.total
  const n = typeof raw === 'number' ? raw : Number(raw ?? 0)
  return Number.isFinite(n) ? n : 0
}

export function sumBuckets(
  buckets: readonly ({ count?: number | null; total?: number | null } | null | undefined)[] | null | undefined,
  metric: Metric = 'count',
): number {
  return (buckets ?? []).reduce((acc, b) => acc + metricValue(b, metric), 0)
}

// ---- In-flight totals (folded out of the status breakdown) -------------------
// §16.1 reports counts and dollars BY STATUS and no in-flight tile, so the two
// in-flight numbers are a fold over the breakdown rather than a field the API
// sends. Same for the dollar side of "reached Point Click Care": the window
// COUNT is a field (`terminal_in_window`), the money is not.

export interface Totals {
  count: number
  total: number
}

function foldStatuses(
  byStatus: readonly StatusBucket[] | null | undefined,
  include: (status: string) => boolean,
): Totals {
  let count = 0
  let total = 0
  for (const b of byStatus ?? []) {
    if (!include(String(b.status))) continue
    count += metricValue(b, 'count')
    total += metricValue(b, 'amount')
  }
  return { count, total: Math.round(total * 100) / 100 }
}

/** Everything still moving: any status that is not one of the five terminals. */
export function inFlightTotals(overview: AnalyticsOverview | null | undefined): Totals {
  return foldStatuses(overview?.by_status, (s) => !TERMINAL_STATUSES.includes(s))
}

/** The terminal side of the same breakdown — the dollars behind the window's
    `terminal_in_window` count. */
export function terminalTotals(overview: AnalyticsOverview | null | undefined): Totals {
  return foldStatuses(overview?.by_status, (s) => TERMINAL_STATUSES.includes(s))
}

/** The statuses behind a set of pipeline nodes — what "awaiting my action" is. */
export function statusTotalsForNodes(
  overview: AnalyticsOverview | null | undefined,
  nodes: ReadonlySet<NodeKey>,
): Totals {
  const wanted = new Set(
    TIMELINE_NODES.filter((n) => nodes.has(n.key)).flatMap((n) => n.queueStatuses as string[]),
  )
  return foldStatuses(overview?.by_status, (s) => wanted.has(s))
}

// ---- Aging bands -------------------------------------------------------------
// The API DEFINES the bands: §16.2 sends each one's label and its day bounds, so
// the app reads them off the response and never hardcodes a threshold. The
// colour comes from the band's POSITION — first is neutral, last is red — which
// stays right if the server ever rebands (docs/app.md → Aging; the colour is
// always paired with the band's own text).

const BAND_COLOR_ORDER: readonly AgeBand[] = ['neutral', 'yellow', 'orange', 'red']

export function bandColorKey(index: number, bandCount: number): AgeBand {
  if (index >= bandCount - 1) return 'red'
  return BAND_COLOR_ORDER[Math.min(index, BAND_COLOR_ORDER.length - 1)] ?? 'neutral'
}

/** Human band text, from the band's own bounds. "31+ days", "8–14 days". */
export function bandLabel(band: AgingBand): string {
  if (band.max_days === null) return `${band.min_days}+ days`
  if (band.min_days === band.max_days) return `${band.min_days} days`
  return `${band.min_days}–${band.max_days} days`
}

/** The band definitions, in order, for the legend. Taken from whichever node
    reported them — the server sends the same set for every node. */
export function agingBands(nodes: readonly AgingNode[] | null | undefined): AgingBand[] {
  for (const node of nodes ?? []) {
    if (node.buckets?.length) return node.buckets.map((b) => ({ ...b, count: 0 }))
  }
  return []
}

/** The pipeline node whose queue holds this status. The status breakdown is
    keyed by status and the aging breakdown by node, so this is the join between
    them. */
export function nodeForStatus(status: string): NodeKey | undefined {
  return TIMELINE_NODES.find((n) => !n.archive && (n.queueStatuses as string[]).includes(status))?.key
}

/** How many invoices sit in the OLDEST band across a set of nodes — the overdue
    number the "awaiting my action" tile carries. Counts only; §16.2 has no money. */
export function oldestBandCount(
  nodes: readonly AgingNode[] | null | undefined,
  wanted?: ReadonlySet<NodeKey>,
): number {
  let sum = 0
  for (const node of nodes ?? []) {
    if (wanted && !wanted.has(node.node)) continue
    const last = node.buckets?.[node.buckets.length - 1]
    if (last) sum += Number(last.count ?? 0)
  }
  return sum
}

// ---- Where invoices are (status bars, stacked by age band) -------------------

export interface StageRow {
  status: string
  label: string
  /** Bar magnitude under the current measure. */
  total: number
  /** One count per age band, in the API's band order. Empty under the money
      measure: §16.2 aggregates counts only, so there is no per-band dollar
      figure to draw and the bar stays a single segment rather than inventing a
      split. */
  bands: number[]
}

/** One row per active status in workflow order, each split into age bands.
    A status with no aging rows (Matching, Matching failed — they have no queue
    node) shows as one segment rather than vanishing. */
export function stageRows(
  byStatus: readonly StatusBucket[] | null | undefined,
  aging: readonly AgingNode[] | null | undefined,
  metric: Metric = 'count',
): StageRow[] {
  const statusTotals = new Map<string, number>()
  for (const b of byStatus ?? []) statusTotals.set(String(b.status), metricValue(b, metric))

  const bandsByNode = new Map<string, number[]>()
  for (const node of aging ?? []) {
    bandsByNode.set(node.node, (node.buckets ?? []).map((b) => Number(b.count ?? 0)))
  }

  const rows: StageRow[] = []
  for (const status of ACTIVE_STATUSES) {
    const label = STAGE_LABELS[status]
    const total = statusTotals.get(status) ?? 0
    if (!label || total === 0) continue
    const node = nodeForStatus(status)
    const bands = metric === 'count' ? ((node && bandsByNode.get(node)) ?? []) : []
    rows.push({ status, label, total, bands })
  }
  return rows
}

/** The oldest band anything is sitting in, and the first stage (in pipeline
    order) sitting there. The aggregate reports bands, not exact ages, so the
    tile reads the band's own text rather than a day count — the band is what
    triage acts on anyway. */
export function oldestWaiting(
  aging: readonly AgingNode[] | null | undefined,
): { band: AgingBand; node: NodeKey; label: string } | null {
  const bands = agingBands(aging)
  for (let i = bands.length - 1; i >= 0; i--) {
    const occupied = (aging ?? []).filter((n) => Number(n.buckets?.[i]?.count ?? 0) > 0)
    if (occupied.length === 0) continue
    const node = TIMELINE_NODES.find((n) => occupied.some((o) => o.node === n.key))
    if (!node) continue
    const band = bands[i]
    if (!band) continue
    return { band, node: node.key, label: node.label }
  }
  return null
}

// ---- Typical wait per stage (restored panel) ---------------------------------
// The source drew a "median days per stage" bar. §16.2 reports BANDED counts,
// not day values, so the honest restoration is the band the middle invoice of
// each stage sits in — the band where the running count crosses half. Labelled
// as a band, never as a day figure the API did not send.

export interface MedianWaitRow {
  node: NodeKey
  label: string
  /** Index into the API's band list — also the colour position. */
  bandIndex: number
  bandLabel: string
  count: number
}

export function medianWaitRows(aging: readonly AgingNode[] | null | undefined): MedianWaitRow[] {
  const rows: MedianWaitRow[] = []
  for (const timelineNode of TIMELINE_NODES) {
    const node = (aging ?? []).find((n) => n.node === timelineNode.key)
    if (!node?.buckets?.length) continue
    const counts = node.buckets.map((b) => Number(b.count ?? 0))
    const total = counts.reduce((a, b) => a + b, 0)
    if (total === 0) continue
    let running = 0
    let index = 0
    for (let i = 0; i < counts.length; i++) {
      running += counts[i] ?? 0
      if (running * 2 >= total) {
        index = i
        break
      }
    }
    const band = node.buckets[index]
    if (!band) continue
    rows.push({
      node: node.node,
      label: timelineNode.label,
      bandIndex: index,
      bandLabel: bandLabel(band),
      count: total,
    })
  }
  return rows
}

// ---- Denial + archive reasons ------------------------------------------------
// The reviewer picks a coarse category alongside the free-text comment on every
// deny and archive (`decisions.reason_category`); this is the consuming half.

/** Decision outcomes that end a record's run: a denial or one of the archives. */
export const DENIAL_OUTCOMES: readonly string[] = [
  DecisionOutcome.Denied,
  DecisionOutcome.ArchivedWithoutPayment,
  DecisionOutcome.ArchivedForFreshInvoice,
  DecisionOutcome.ArchivedNotAnInvoice,
]

export interface RankedRow {
  key: string
  value: number
}

const UNCATEGORIZED = 'Not categorized'

export interface DenialReasons {
  rows: RankedRow[]
  /** Denials and archives by the step they happened at. */
  steps: RankedRow[]
  total: number
  /** How many carried a category — the rest are "Not categorized". */
  categorized: number
}

/** Most common reason first, except that the uncategorized bucket never
    outranks a real reason — it is context, not a finding. It is still shown:
    dropping it would make a chart of nothing but uncategorized denials read as
    "no denials". */
export function denialReasonRows(agg: AnalyticsDenials | null | undefined, topN = 6): DenialReasons {
  const byCategory = agg?.by_reason_category ?? []
  let total = 0
  let categorized = 0
  const counts = new Map<string, number>()
  for (const r of byCategory) {
    const n = Number(r.count ?? 0)
    if (!Number.isFinite(n)) continue
    total += n
    const key = r.reason_category?.trim()
    if (key) categorized += n
    const label = key ? reasonCategoryLabel(key) : UNCATEGORIZED
    counts.set(label, (counts.get(label) ?? 0) + n)
  }
  const rows = [...counts.entries()]
    .sort((a, b) => (a[0] === UNCATEGORIZED ? 1 : b[0] === UNCATEGORIZED ? -1 : b[1] - a[1]))
    .slice(0, topN)
    .map(([key, value]) => ({ key, value }))
  // "Director Approval Decision" is the stored step; the chart shows the tier.
  const steps = (agg?.by_step ?? [])
    .map((r) => ({ key: String(r.step ?? '').replace(/ Decision$/, ''), value: Number(r.count ?? 0) }))
    .filter((r) => r.key && Number.isFinite(r.value))
    .sort((a, b) => b.value - a.value)
  return { rows, steps, total, categorized }
}

// ---- Spend by entity ---------------------------------------------------------

/** "Type: name" rows, biggest spend first. A line tagged with several entities
    credits each of them its full total — the point is per-entity cost, not a
    partition — and the API does that attribution. */
export function spendRows(agg: AnalyticsSpendByEntity | null | undefined, topN = 8): RankedRow[] {
  return (agg?.entities ?? [])
    .map((r) => ({
      // `entity_name` is the friendly name; `entity_key` is the resolved
      // identity the tag actually stores, and the fallback when there is no name.
      key: `${r.entity_type}: ${r.entity_name?.trim() || r.entity_key?.trim() || '(unresolved)'}`,
      value: metricValue({ total: r.total }, 'amount'),
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, topN)
}

// ---- Corrections (the feedback loop's own numbers) --------------------------

/** Human labels for the fields that show up as correction targets. */
const CORRECTION_FIELD_LABELS: Record<string, string> = {
  vendor_id: 'Vendor',
  company_id: 'Company',
  // The GL account is three correctable fields now: the two halves, edited
  // separately by different people at different steps, and the composed value
  // written whole at Export Control and Manual Entry.
  expense_type_code: 'Expense type',
  cost_center_code: 'Cost center',
  final_account_id: 'GL account',
  external_invoice_id: 'Invoice #',
  invoice_date: 'Date',
  subtotal: 'Subtotal',
  tax: 'Tax',
  total: 'Total',
  description: 'Line description',
  quantity: 'Quantity',
  unit_price: 'Price',
  LineAdded: 'Line added',
  LineRemoved: 'Line removed',
}

export function correctionFieldLabel(field: string | undefined | null): string {
  return (field && CORRECTION_FIELD_LABELS[field]) || field || '(unknown)'
}

/**
 * The label for one `(field, old_source)` bucket.
 *
 * `old_source` must never be read alone: it mirrors `expense_type_source` on an
 * expense-type change and `cost_center_source` on a cost-center change, and the
 * two enums share `VendorRule`, `DocumentPrinted` and `Manual`. `ChartImplied`
 * belongs only to the cost center and would otherwise sit in the expense-type
 * distribution as though the model or a reviewer had produced it.
 */
export function correctionSourceLabel(
  field: string | undefined | null,
  source: string | undefined | null,
): string {
  const half =
    field === 'cost_center_code'
      ? 'Cost center'
      : field === 'expense_type_code'
        ? 'Expense type'
        : field === 'final_account_id'
          ? 'GL account'
          : null
  const label = source
    ? (field === 'cost_center_code' ? costCenterSourceLabel(source) : accountSourceLabel(source))
    : 'Not set'
  return half === null ? label : `${half} — ${label}`
}

export interface CorrectionPanel {
  /** Where the pipeline errs most often. */
  fields: RankedRow[]
  /** Which vendors' documents get corrected most. */
  vendors: RankedRow[]
  /** What the corrected value had been set from — precedent, a vendor default,
      the model — so a source that keeps being wrong is visible. */
  sources: RankedRow[]
  total: number
  /** Flags cleared with no correction at all, per flag type — the per-flag
      precision table, straight from the API. Ranked, worst first. */
  flagPrecision: RankedRow[]
  /** Every clean resolution, whichever flag raised it. */
  flagsResolvedClean: number
}

export function correctionRows(agg: AnalyticsCorrections | null | undefined, topN = 8): CorrectionPanel {
  const rank = (rows: { key: string; count: number }[]) =>
    rows
      .filter((r) => r.key && Number.isFinite(r.count))
      .sort((a, b) => b.count - a.count)
      .slice(0, topN)
      .map((r) => ({ key: r.key, value: r.count }))

  const fields = rank(
    (agg?.by_field ?? []).map((r) => ({ key: correctionFieldLabel(r.field), count: Number(r.count ?? 0) })),
  )
  const vendors = rank(
    (agg?.by_vendor ?? []).map((r) => ({
      key: r.vendor_name?.trim() || r.vendor_id || '(unknown vendor)',
      count: Number(r.count ?? 0),
    })),
  )
  // Keyed on (field, old_source), because `old_source` alone is ambiguous: the
  // two source enums share `VendorRule`, `DocumentPrinted` and `Manual`, so a
  // cost-center `Manual` and an expense-type `Manual` are different facts. The
  // half is named in the label so the bar chart stays readable.
  const sources = rank(
    (agg?.by_old_source ?? []).map((r) => ({
      key: correctionSourceLabel(r.field, r.old_source),
      count: Number(r.count ?? 0),
    })),
  )
  const clean = agg?.flags_resolved_without_corrections ?? []
  return {
    fields,
    vendors,
    sources,
    // Every correction in the window, not just the top fields shown.
    total: (agg?.by_field ?? []).reduce((acc, r) => acc + (Number(r.count ?? 0) || 0), 0),
    flagPrecision: rank(
      clean.map((r) => ({ key: flagLabel(r.flag_type) || r.flag_type, count: Number(r.count ?? 0) })),
    ),
    flagsResolvedClean: clean.reduce((acc, r) => acc + (Number(r.count ?? 0) || 0), 0),
  }
}
